'use client'

import { useState, useEffect } from 'react'
import { HOME_SECTIONS } from '@/content/demo-home'
import { ctaSousPage } from '@/lib/composition/annonceSoiree'
import { ADMIN_API } from '@/lib/reservationForm'

/**
 * Navigation des sous-pages : flèche retour (revient à la page précédente en
 * restaurant la position) + bouton burger qui ouvre le menu plein écran.
 * Pas de logo/navbar complète (demande cliente) — juste retour + menu.
 * Les liens reprennent ceux du header de la home.
 */
const ACCENT = '#C2A66B'
const CREAM = '#F7EFE1'

type Link = { id: string; label: string; href: string }
const headerContent = (HOME_SECTIONS.find((s) => s.type === 'site-header')?.content ?? {}) as {
  links?: Link[]
  ctaLabel?: string
  ctaHref?: string
}

const pill: React.CSSProperties = {
  position: 'fixed',
  top: 18,
  zIndex: 60,
  width: 42,
  height: 42,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(4,10,24,0.35)',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
  border: '1px solid rgba(247,239,225,0.25)',
  borderRadius: 999,
  color: CREAM,
  cursor: 'pointer',
  transition: 'background 0.3s, border-color 0.3s',
}

export default function SubPageNav() {
  const [open, setOpen] = useState(false)
  /* `null` = on ne sait pas encore. Le bouton « Réserver » du menu mène à
     `/#formules`, une section de l'accueil qui disparaît avec la soirée : on
     ne l'affiche donc qu'une fois la soirée confirmée à venir (cf.
     `lib/composition/annonceSoiree.ts`, incident du 2026-10-05). */
  const [soireeAVenir, setSoireeAVenir] = useState<boolean | null>(null)

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [open])

  useEffect(() => {
    let vivant = true
    fetch(`${ADMIN_API}/api/public/current-event`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (vivant) setSoireeAVenir(!!d?.event && d.event.status !== 'past') })
      /* Injoignable : on reste sur « aucun bouton ». Mieux vaut un menu sans
         bouton qu'un bouton qui ne mène nulle part. */
      .catch(() => { if (vivant) setSoireeAVenir(false) })
    return () => { vivant = false }
  }, [])

  const links = headerContent.links ?? []
  const cta = ctaSousPage(headerContent, soireeAVenir)
  // On ne revient en arrière que si la page précédente est une page du site.
  // `history.length` ne suffit pas : un lien ouvert depuis WhatsApp ou un
  // nouvel onglet a déjà une entrée (onglet vide) — « retour » y menait,
  // d'où une page blanche (retour client du 21/09 sur /r/<slug>).
  const goBack = () => {
    if (typeof window === 'undefined') return
    let depuisLeSite = false
    try {
      depuisLeSite = !!document.referrer && new URL(document.referrer).origin === window.location.origin
    } catch {
      depuisLeSite = false
    }
    if (depuisLeSite && window.history.length > 1) window.history.back()
    else window.location.href = '/'
  }

  return (
    <>
      {/* Flèche retour (gauche) */}
      <button type="button" onClick={goBack} aria-label="Retour" style={{ ...pill, left: 18 }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
      </button>

      {/* Burger (droite) */}
      <button type="button" onClick={() => setOpen(true)} aria-label="Ouvrir le menu" style={{ ...pill, right: 18 }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <line x1="4" y1="8" x2="20" y2="8" />
          <line x1="4" y1="16" x2="20" y2="16" />
        </svg>
      </button>

      {/* Menu plein écran */}
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 70,
          background: 'rgba(2,12,28,0.975)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'clamp(16px, 2.6vh, 26px)',
          opacity: open ? 1 : 0,
          visibility: open ? 'visible' : 'hidden',
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 0.35s ease, visibility 0.35s ease',
        }}
      >
        <button type="button" onClick={() => setOpen(false)} aria-label="Fermer" style={{ position: 'absolute', top: 18, right: 18, width: 44, height: 44, background: 'none', border: '1px solid rgba(247,239,225,0.3)', borderRadius: 999, color: CREAM, fontSize: 24, lineHeight: 1, cursor: 'pointer' }}>×</button>
        {links.map((l) => (
          <a
            key={l.id}
            href={l.href}
            onClick={() => setOpen(false)}
            style={{ fontFamily: 'var(--font-heading, inherit)', fontSize: 'clamp(22px, 5vw, 30px)', color: CREAM, textDecoration: 'none', letterSpacing: '0.02em' }}
          >
            {l.label}
          </a>
        ))}
        {cta && (
          <a
            href={cta.href}
            onClick={() => setOpen(false)}
            style={{ marginTop: 14, fontSize: 12, letterSpacing: '0.26em', textTransform: 'uppercase', fontWeight: 600, color: ACCENT, borderBottom: `1px solid ${ACCENT}`, paddingBottom: 5, textDecoration: 'none' }}
          >
            {cta.label}
          </a>
        )}
      </div>
    </>
  )
}
